import { useNavigate, useParams } from 'react-router';
import { cn } from 'helpers/cn';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import { useSurvey } from '@/state/surveys/useSurvey';
import { questionsOf, useSurveyBuilder } from '@/state/surveys/useSurveyBuilder';
import { canonicalQuestions, questionsForSave } from '@/state/surveys/survey-questions';
import { experimentLinkChanged, targetingChanged, targetingForSave } from '@/state/surveys/survey-targeting';
import { StepTemplate } from './StepTemplate';
import { StepQuestions } from './StepQuestions';
import { StepTargeting } from './StepTargeting';
import { StepTrigger } from './StepTrigger';
import { StepTheme } from './StepTheme';
import { SurveyPreview } from './SurveyPreview';

/**
 * Why Continue is dead, per step.
 *
 * A greyed-out Continue with no message is a hard stop in a creation wizard: the
 * step pills tick off what is *done* but nothing names the field that is wrong, so
 * a reader who cannot advance has no way to find out why. These mirror the rules in
 * `stepValidityFor`, one sentence each.
 */
const STEP_BLOCKED_REASON: Record<string, string> = {
  Template: 'Pick a survey type and give the survey a name',
  // Questions, Targeting and Theme name their own reason (questionsProblem,
  // targetingProblem, themeProblem), since each has several.
  Trigger: 'Check the trigger value: a delay is 0 to 600 seconds, a scroll depth 1 to 100%, and an event needs a name',
};

export const SurveyBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const surveyId = id && id !== 'new' ? Number(id) : 0;
  const { survey, isLoading, isSaving, createSurvey, updateSurvey } = useSurvey(surveyId);

  // Wait for the row before mounting the form: useForm seeds its initial state
  // once, so mounting with an empty draft and filling it later would leave the
  // DOM and the state out of step.
  if (surveyId && (isLoading || !survey)) return null;

  return (
    <Builder
      key={surveyId}
      existingId={surveyId}
      survey={survey}
      isSaving={isSaving}
      onCreate={createSurvey}
      onUpdate={updateSurvey}
      navigate={navigate}
    />
  );
};

type BuilderProps = {
  existingId: number;
  survey: ReturnType<typeof useSurvey>['survey'];
  isSaving: boolean;
  onCreate: ReturnType<typeof useSurvey>['createSurvey'];
  onUpdate: ReturnType<typeof useSurvey>['updateSurvey'];
  navigate: ReturnType<typeof useNavigate>;
};

const Builder = ({ existingId, survey, isSaving, onCreate, onUpdate, navigate }: BuilderProps) => {
  const manager = useSurveyBuilder(survey);
  const {
    form,
    draft,
    step,
    setStep,
    steps,
    stepIndex,
    stepValidity,
    questionsProblem,
    themeProblem,
    targetingProblem,
    canAdvance,
    canSubmit,
    isFirst,
    isLast,
    next,
    back,
  } = manager;

  const stepReason =
    step === 'Questions'
      ? questionsProblem
      : step === 'Targeting'
        ? targetingProblem
        : step === 'Theme'
          ? themeProblem
          : STEP_BLOCKED_REASON[step];
  const unfinished = steps.filter((name) => !stepValidity[name]);
  // Shown as text, not only as a tooltip: a disabled button cannot take focus, a
  // title never appears on touch, and screen readers do not announce it.
  const blockedReason = isLast
    ? unfinished.length > 0
      ? `Finish the earlier steps first: ${unfinished.join(', ')}`
      : null
    : canAdvance
      ? null
      : (stepReason ?? null);

  const submit = async () => {
    const toSave = { ...draft, questions: questionsForSave(draft), targeting: targetingForSave(draft) };
    if (existingId) {
      // Send questions ONLY when they actually changed.
      //
      // The questions route rejects a Running survey, because rewriting question
      // ids would orphan the responses already collected against them. Sending
      // them unconditionally meant editing a running survey's theme or trigger
      // always failed with a 409, even though nothing about the questions had
      // been touched.
      const saved = survey ? questionsOf(survey) : [];
      // Compared on meaning, not raw JSON (canonicalQuestions), so an emptied field or
      // a reordered config key cannot make an unchanged survey look edited.
      const questionsChanged =
        JSON.stringify(canonicalQuestions(toSave.questions)) !== JSON.stringify(canonicalQuestions(saved));

      const linkChanged = !survey || experimentLinkChanged({ draft: toSave, saved: survey });
      await onUpdate(existingId, {
        ...toSave,
        questions: questionsChanged ? toSave.questions : undefined,
        targeting: !survey || targetingChanged(toSave.targeting, survey.targeting) ? toSave.targeting : undefined,
        // The pair travels together or not at all (the API requires it).
        experimentId: linkChanged ? toSave.experimentId : undefined,
        variantIds: linkChanged ? toSave.variantIds : undefined,
      });
      // Back to the survey's own hub, which is where the editor is now entered from.
      navigate(`/surveys/${existingId}`);
      return;
    }
    const created = await onCreate(toSave);
    if (created) navigate('/surveys/library');
  };

  return (
    <main className="flex flex-col gap-[14px]">
      <section className="bg-panel border-line rounded-panel border px-4 py-3">
        <ol className="m-0 -mx-1 flex list-none flex-wrap items-center gap-1.5 px-1" data-tour="survey-builder-steps">
          {steps.map((name, index) => {
            const isCurrent = name === step;
            const isDone = stepValidity[name] && index < stepIndex;
            return (
              <li key={name}>
                <button
                  type="button"
                  // Any step is reachable at any time. A wizard that locks later
                  // steps until earlier ones are perfect is hostile when you are
                  // editing an existing survey and only want to change the theme.
                  onClick={() => setStep(name)}
                  className={cn(
                    // min-h-9 clears the 36px touch floor on a phone; border-input on an
                    // unselected step, which has no fill for --line to sit against.
                    'micro-label rounded-pill flex min-h-9 items-center gap-2 border py-1.5 pl-1.5 pr-3',
                    'transition-colors duration-150 md:min-h-0',
                    isCurrent
                      ? 'bg-sel-bg border-sel-line text-foreground'
                      : 'border-input text-dim hover:bg-hover hover:text-foreground',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full border',
                      isDone ? 'border-ok-line text-ok' : 'border-line',
                    )}
                  >
                    {isDone ? <Check className="h-3 w-3" /> : index + 1}
                  </span>
                  {name}
                </button>
              </li>
            );
          })}
        </ol>
      </section>

      <div className="grid gap-[14px] lg:grid-cols-[minmax(0,1fr)_360px]">
        {/*
          ONE form for the entire wizard, with every step mounted and the
          inactive ones hidden with CSS.

          useForm resolves [name=…] against the document rather than the form
          element, so two forms mounted at once that share a field name write into
          the wrong input silently. Rendering a form per step is exactly that
          shape. Keeping one form also means state survives step navigation with
          no draft merging.
        */}
        <form
          ref={form.formRef}
          className="bg-panel border-line rounded-panel flex flex-col border"
          onSubmit={(ev) => ev.preventDefault()}
        >
          <div className="p-4">
            <div hidden={step !== 'Template'}>
              <StepTemplate manager={manager} />
            </div>
            <div hidden={step !== 'Questions'}>
              <StepQuestions manager={manager} />
            </div>
            <div hidden={step !== 'Targeting'}>
              <StepTargeting manager={manager} />
            </div>
            <div hidden={step !== 'Trigger'}>
              <StepTrigger manager={manager} />
            </div>
            <div hidden={step !== 'Theme'}>
              <StepTheme manager={manager} />
            </div>
          </div>

          {/* gap + flex-wrap: three verbs on a 343px line is tight enough that a longer
              primary label ("Create survey") has to be allowed to drop rather than run
              past the panel edge. border-input on Back — no fill, so the border is all
              that draws it. */}
          <div
            className="border-line bg-panel2 rounded-b-panel flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3"
            data-tour="survey-builder-actions"
          >
            <Button
              type="button"
              variant="outline"
              onClick={back}
              disabled={isFirst}
              className="border-input h-9 rounded-lg bg-transparent"
            >
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(existingId ? `/surveys/${existingId}` : '/surveys/library')}
                className="text-dim hover:text-foreground hover:bg-hover h-9 rounded-lg"
              >
                Cancel
              </Button>
              {!isLast && (
                <Button
                  type="button"
                  onClick={next}
                  disabled={!canAdvance}
                  aria-describedby={blockedReason ? 'survey-builder-blocked' : undefined}
                  className="h-9 rounded-lg"
                >
                  Continue
                </Button>
              )}
              {isLast && (
                <Button
                  type="button"
                  onClick={submit}
                  disabled={!canSubmit || isSaving}
                  aria-describedby={blockedReason ? 'survey-builder-blocked' : undefined}
                  className="h-9 rounded-lg"
                >
                  {existingId ? 'Save changes' : 'Create survey'}
                </Button>
              )}
            </div>
            {blockedReason && (
              <p id="survey-builder-blocked" role="status" className="text-danger m-0 w-full text-micro">
                {blockedReason}
              </p>
            )}
          </div>
        </form>

        <aside className="bg-panel border-line rounded-panel flex flex-col border" data-tour="survey-builder-preview">
          <div className="border-line border-b px-4 py-3">
            <h2 className="text-foreground m-0 text-body font-medium">Preview</h2>
          </div>
          <div className="space-y-2 p-3">
            <SurveyPreview draft={draft} />
            <p className="supporting m-0">
              A representation, not the shipped widget. The real one renders in a shadow root on the customer's page.
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
};
